import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ScrollBox } from 'components/atoms/ScrollBox';
import styled from 'styled-components';
import { Box } from 'rebass/styled-components';
import KeywordFilter from '../../../components/atoms/KeywordFilter';
import { Accordion } from '../../../components/organisms/contactsFilters/components/accordion';
import { createHiresFilterGroups } from './hiresFilterGroups';

const AccordionContainer = styled.div`
  width: 100%;

  && label {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.main};
  }

  && > div > div:last-child {
    border-bottom: none;
  }
`;

const countHiresFilters = (filtersToCount) => {
  let count = 0;
  Object.keys(filtersToCount).forEach((key) => {
    const value = filtersToCount[key];

    // Skip null, undefined
    if (value === null || value === undefined) return;

    // Handle arrays
    if (Array.isArray(value) && value.length > 0) {
      count += 1;
      return;
    }

    // Handle objects (e.g., { value: 'any' } or { startDate, endDate })
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Skip if object has value property that is 'any' or empty
      if (value.value === 'any' || value.value === '' || value.value === null) return;
      // Count if object has meaningful data
      if (Object.keys(value).length > 0) count += 1;
      return;
    }

    // Skip empty strings and 'any'
    if (value === '' || value === 'any') return;

    // Count everything else
    count += 1;
  });
  return count;
};

export const HiresFilters = memo(({ filters, setFilters, keyword, onKeywordChange: onKeywordChangeProp }) => {
  const keywordArray = keyword ? [keyword] : [];
  const onKeywordChange = useCallback(
    (value) => {
      onKeywordChangeProp(Array.isArray(value) ? value[0] || '' : value || '');
    },
    [onKeywordChangeProp],
  );
  const setMultipleFilters = useCallback(
    (group) => {
      // Pass filter changes directly to parent setFilters
      // Parent will handle merging with existing filters
      setFilters(group);
    },
    [setFilters],
  );

  const groups = useMemo(
    () =>
      createHiresFilterGroups({
        filters,
        setFilters: setMultipleFilters,
      }).reduce((acc, { id: groupId, title, show, filters: groupFilters }) => {
        if (show) {
          const filtersToCount = {};
          const currentGroup = {
            id: groupId,
            title,
            content: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {groupFilters.reduce(
                  (acc2, { id: filterId, Component, props, show: showFilter, additionalCountKeys }) => {
                    if (showFilter && Component) {
                      acc2.push(<Component key={filterId} {...props} />);

                      // Count filters for badge - skip any, empty, and null values
                      const filterValue = filters[filterId];

                      if (filterValue !== null && filterValue !== undefined) {
                        if (Array.isArray(filterValue) && filterValue.length > 0) {
                          filtersToCount[filterId] = filterValue;
                        } else if (typeof filterValue === 'object' && !Array.isArray(filterValue)) {
                          // Handle objects - skip if value property is 'any' or empty
                          if (filterValue.value !== 'any' && filterValue.value !== '' && filterValue.value !== null) {
                            if (Object.keys(filterValue).length > 0) {
                              filtersToCount[filterId] = filterValue;
                            }
                          }
                        } else if (!Array.isArray(filterValue) && filterValue !== '' && filterValue !== 'any') {
                          filtersToCount[filterId] = filterValue;
                        }
                      }

                      // Include additional filter keys for counting - apply same any/empty exclusions
                      if (additionalCountKeys) {
                        additionalCountKeys.forEach((key) => {
                          const additionalValue = filters[key];
                          if (additionalValue !== null && additionalValue !== undefined) {
                            if (Array.isArray(additionalValue) && additionalValue.length > 0) {
                              filtersToCount[key] = additionalValue;
                            } else if (typeof additionalValue === 'object' && !Array.isArray(additionalValue)) {
                              if (
                                additionalValue.value !== 'any' &&
                                additionalValue.value !== '' &&
                                additionalValue.value !== null
                              ) {
                                if (Object.keys(additionalValue).length > 0) {
                                  filtersToCount[key] = additionalValue;
                                }
                              }
                            } else if (
                              !Array.isArray(additionalValue) &&
                              additionalValue !== '' &&
                              additionalValue !== 'any'
                            ) {
                              filtersToCount[key] = additionalValue;
                            }
                          }
                        });
                      }
                    }

                    return acc2;
                  },
                  [],
                )}
              </div>
            ),
          };

          const count = countHiresFilters(filtersToCount);
          if (count > 0) {
            currentGroup.badge = `${count}`;
          }

          acc.push(currentGroup);
        }

        return acc;
      }, []),
    [filters, setMultipleFilters],
  );

  return (
    <ScrollBox
      data-testid="hires-filters"
      sx={{
        top: 0,
        height: '100%',
        padding: '0 16px',
      }}
    >
      <Box
        sx={{
          gap: '12px',
          position: 'relative',
          paddingBottom: '16px',
        }}
      >
        <Box sx={{ width: '100%', mb: '12px' }} data-testid="hires-filters-keywords">
          <KeywordFilter onChange={onKeywordChange} value={keywordArray} />
        </Box>
        <AccordionContainer>
          <Accordion items={groups} />
        </AccordionContainer>
      </Box>
    </ScrollBox>
  );
});

HiresFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  keyword: PropTypes.string,
  onKeywordChange: PropTypes.func.isRequired,
};

HiresFilters.defaultProps = {
  keyword: '',
};
